import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector, type AppDispatch } from '@/app/store/store';
import { selectFooterInViewport } from '@/app/store/footerVisibilitySlice';
import {
  clearPlpInitialData,
  selectPlpInitialData,
  selectPlpInitialUrl,
} from '@/features/plp/presentation/store/plpInitialSlice';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { plpBuild } from '@/features/plp/config/plp.build';
import { filtersBuild } from '@/features/filters/config/filters.build';
import { usePlpListing } from '@/features/plp/presentation/hooks/usePlpListing';
import { usePlpRedirectUrlNavigation } from '@/features/plp/presentation/hooks/usePlpRedirectUrlNavigation';
import { usePlpSidQueryHandlers } from '@/features/plp/presentation/hooks/usePlpSidQueryHandlers';
import { usePlpPincode } from '@/features/plp/presentation/hooks/usePlpPincode';
import { usePlpFilters } from '@/features/filters/presentation/hooks/usePlpFilters';
import { usePlpDesktopFilters } from '@/features/filters/presentation/hooks/usePlpDesktopFilters';
import { usePlpPresentation } from '@/features/plp/presentation/hooks/usePlpPresentation';
import { usePlpAnalytics } from '@/features/plp/presentation/hooks/usePlpAnalytics';
import { useVideoCallCart } from '@/features/video-call/presentation/hooks/useVideoCallCart';
import { useIsDesktop, useIsMobile } from '@/core/hooks/useMediaQuery';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { clearStoreLocationWidgetSession } from '@/shared/features/physical-store/presentation/hooks/useStoreLocationWidget';
import type { ListingPageResult } from '@/features/plp/domain/models/ProductListing';
import type { GetFiltersParams } from '@/features/filters/application/services/FiltersService';
import {
  getPlpListingKey,
  getPlpBrowseTagsForApi,
  getPlpApiQueryContextFromSearch,
  getPreferredProductsFromSearch,
} from '@/features/plp/utils/plpUrl';
import { PLP_DEFAULT_SORT, PLP_PAGE_TYPE, type PlpSortByValue } from '@/features/plp/constants';

import { PlpBase } from '@/features/plp/presentation/components/PlpBase';
import { PlpSortSheet } from '@/features/plp/presentation/components/PlpSortSheet';
import { PlpFilterSheet } from '@/features/filters/presentation/components/PlpFilterSheet';
import { PlpDesktopFilterSidebar } from '@/features/filters/presentation/components/PlpDesktopFilterSidebar';
import { PincodePopup } from '@/shared/features/pincode/presentation/components/PincodePopup';
import { VideoCallStickyBar } from '@/features/video-call/presentation/components/VideoCallStickyBar';
import { PlpScrollToTopButton } from '@/shared/widgets/PlpScrollToTopButton';

function getSidFromSearch(search: string): string | undefined {
  const raw = new URLSearchParams(search).get('sid');
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

function getSortByFromSearch(search: string): PlpSortByValue {
  const raw = new URLSearchParams(search).get('sortby')?.trim().toLowerCase();
  if (!raw) return PLP_DEFAULT_SORT as PlpSortByValue;
  const exists = (['date_add', 'mostpopular', 'pricelow', 'pricehigh', 'discount'] as PlpSortByValue[]).includes(
    raw as PlpSortByValue,
  );
  return exists ? (raw as PlpSortByValue) : (PLP_DEFAULT_SORT as PlpSortByValue);
}

export default function BrowsePlpPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || '/plp';
  const params = useParams<{ tagString?: string }>();

  const sidFromUrl = useMemo(() => getSidFromSearch(location.search), [location.search]);
  const sortBy = useMemo(() => getSortByFromSearch(location.search), [location.search]);
  const preferredProducts = useMemo(() => getPreferredProductsFromSearch(location.search), [location.search]);
  const apiQueryContext = useMemo(() => getPlpApiQueryContextFromSearch(location.search), [location.search]);
  const isFiltered = pathname.includes('/filter/');
  const isGoldCoinsPlp = /gold[+\-%2B]?coins?/i.test(pathname);

  const tags = useMemo(() => {
    if (params.tagString != null && params.tagString !== '') return params.tagString;
    return getPlpBrowseTagsForApi(pathname);
  }, [pathname, params.tagString]);

  const { ref: topSentinelRef, isIntersecting: isTopVisible } = useIntersectionObserver<HTMLDivElement>();
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const footerInViewport = useAppSelector(selectFooterInViewport);
  const shouldPauseInfiniteScroll = useCallback(() => footerInViewport, [footerInViewport]);

  const { service } = useMemo(() => plpBuild(dispatch), [dispatch]);
  const { filtersService } = useMemo(() => filtersBuild(dispatch), [dispatch]);

  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);
  const [isNoStoreFound, setIsNoStoreFound] = useState(false);
  const [pincodeDescription, setPincodeDescription] = useState<string | undefined>(undefined);

  const {
    pincode,
    sid,
    inputValue,
    error: pincodeError,
    isPopupOpen,
    openPopup,
    closePopup,
    handleChange,
    handleSubmit,
  } = usePlpPincode();

  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    return () => {
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    };
  }, []);

  useEffect(() => {
    return () => {
      clearStoreLocationWidgetSession();
    };
  }, []);

  const initialPlpData = useSelector(selectPlpInitialData);
  const initialPlpUrl = useSelector(selectPlpInitialUrl);
  const plpKey = useMemo(() => getPlpListingKey(pathname, location.search), [pathname, location.search]);

  const ssrInitialDataMatchesRoute =
    initialPlpData != null &&
    initialPlpUrl != null &&
    getPlpListingKey(initialPlpUrl) === plpKey;

  const effectiveInitialData = sortBy !== PLP_DEFAULT_SORT || !ssrInitialDataMatchesRoute ? null : initialPlpData;

  useEffect(() => {
    if (initialPlpData != null) {
      dispatch(clearPlpInitialData());
    }
    // Run once after first render to drop SSR data so remounts refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listingSid = sidFromUrl ?? apiQueryContext.sid;

  const fetchPage = useCallback(
    async (pageNumber: number, opts?: { signal?: AbortSignal }): Promise<ListingPageResult> => {
      const result = await service.getListing({
        pageNumber,
        tags,
        tags_to_sort_filters: tags,
        sortBy,
        page_type: PLP_PAGE_TYPE.BROWSE,
        ...(preferredProducts ? { preferredProducts } : {}),
        is_filtered: isFiltered,
        sid: listingSid,
        storeIds: apiQueryContext.storeIds,
        wid: apiQueryContext.wid,
        url_query_string: apiQueryContext.url_query_string,
        design_ids: apiQueryContext.design_ids,
        ...(opts?.signal ? { signal: opts.signal } : {}),
      });
      return result as unknown as ListingPageResult;
    },
    [service, tags, sortBy, preferredProducts, isFiltered, listingSid, apiQueryContext],
  );

  const filterParams = useMemo(
    (): Omit<GetFiltersParams, 'tags'> => ({
      page_type: PLP_PAGE_TYPE.BROWSE,
      tags_to_sort_filters: tags,
      is_filtered: isFiltered,
      is_for_filter_change: false,
      sid: listingSid,
      storeIds: apiQueryContext.storeIds,
      wid: apiQueryContext.wid,
      url_query_string: apiQueryContext.url_query_string,
      design_ids: apiQueryContext.design_ids,
    }),
    [tags, isFiltered, listingSid, apiQueryContext],
  );

  const plp = usePlpListing({
    fetchPage,
    pathname,
    initialData: effectiveInitialData,
    listingKey: plpKey,
    restoreKey: plpKey,
    isMobile,
    shouldPauseInfiniteScroll,
  });

  const plpAnalytics = usePlpAnalytics({
    pageType: 'browse',
    listingHeader: plp.listingMeta?.header ?? '',
    collectionTags: tags,
    currentSortBy: sortBy,
    totalCount: plp.listingMeta?.totalCount ?? 0,
  });

  const pageViewFiredRef = useRef<string | null>(null);
  useEffect(() => {
    plpAnalytics.viewedProductIds.clear();
  }, [plpKey, plpAnalytics.viewedProductIds]);

  useEffect(() => {
    if (!plp.listingMeta) return;
    if (plp.allDesigns.length === 0) return;
    if (!plpAnalytics.isReady) return;
    if (pageViewFiredRef.current === plpKey) return;
    pageViewFiredRef.current = plpKey;
    plpAnalytics.trackPageView();
  }, [plp.listingMeta, plp.allDesigns.length, plpKey, plpAnalytics]);

  useEffect(() => {
    const handleScroll = () => plpAnalytics.trackScrollDown();
    window.addEventListener('scroll', handleScroll, { passive: true, once: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [plpAnalytics]);

  const { handleActivateStoreDesigns, handleShowAllDesigns } = usePlpSidQueryHandlers({
    sidFromUrl,
    sid,
    pincode,
    onOpenPincodePopup: openPopup,
    onNoStoreFound: () => setIsNoStoreFound(true),
  });

  const applySortToUrl = useCallback(
    (nextSort: PlpSortByValue) => {
      const urlParams = new URLSearchParams(location.search);
      urlParams.set('sortby', nextSort);
      navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` }, { replace: false });
    },
    [location.pathname, location.search, navigate],
  );

  const handleOpenSortSheet = useCallback(() => {
    plpAnalytics.trackSortFilterClicked();
    setIsSortSheetOpen(true);
  }, [plpAnalytics]);

  const handleApplySortFromSheet = useCallback(
    (nextSort: PlpSortByValue) => {
      plpAnalytics.trackSortApply(nextSort);
      applySortToUrl(nextSort);
      setIsSortSheetOpen(false);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [applySortToUrl, plpAnalytics],
  );

  const handleApplySort = useCallback(
    (nextSort: PlpSortByValue) => {
      plpAnalytics.trackSortApply(nextSort);
      applySortToUrl(nextSort);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [applySortToUrl, plpAnalytics],
  );

  const handleOpenBottomPincode = useCallback(() => {
    plpAnalytics.trackPincodeClick(pincode ?? '');
    setPincodeDescription('Browse our exquisite designs: locate stores near you!');
    if (sidFromUrl) {
      openPopup((nextSid) => {
        if (nextSid) {
          const urlParams = new URLSearchParams(location.search);
          urlParams.set('sid', nextSid);
          navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` });
        } else {
          const urlParams = new URLSearchParams(location.search);
          urlParams.delete('sid');
          setIsNoStoreFound(true);
          navigate({ pathname: location.pathname, search: urlParams.toString() ? `?${urlParams.toString()}` : '' });
        }
      });
    } else if (isNoStoreFound) {
      openPopup((nextSid) => {
        if (nextSid) {
          setIsNoStoreFound(false);
          const urlParams = new URLSearchParams(location.search);
          urlParams.set('sid', nextSid);
          navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` });
        } else {
          setIsNoStoreFound(true);
        }
      });
    } else {
      openPopup();
    }
  }, [openPopup, plpAnalytics, pincode, sidFromUrl, isNoStoreFound, location.search, location.pathname, navigate]);

  const handlePincodeSubmit = useCallback(async () => {
    plpAnalytics.trackPincodeEntered(inputValue, !/^[1-9][0-9]{5}$/.test(inputValue.trim()));
    await handleSubmit();
  }, [plpAnalytics, inputValue, handleSubmit]);

  const handlePincodeOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openPopup();
      } else {
        setPincodeDescription(undefined);
        closePopup();
      }
    },
    [openPopup, closePopup],
  );

  const { metaPresentation, breadcrumbItems, storeCountLabel } = usePlpPresentation({
    listingMeta: plp.listingMeta,
    pathname,
    isStorePlp: false,
    isSearchPlp: false,
  });

  const displayListingMeta = useMemo(() => {
    if (!plp.listingMeta) return null;
    if (!metaPresentation) return plp.listingMeta;
    return {
      ...plp.listingMeta,
      header: metaPresentation.headerLabel,
      ...(metaPresentation.filtersResetUrl && { filtersResetUrl: metaPresentation.filtersResetUrl }),
      ...(metaPresentation.exploreLinks?.length && { exploreLinks: metaPresentation.exploreLinks }),
      ...(metaPresentation.listingSeo && { listingSeo: metaPresentation.listingSeo }),
    };
  }, [plp.listingMeta, metaPresentation]);

  const {
    isOpen: isFilterSheetOpen,
    openFilterSheet,
    previewFilters,
    selectedOptions: filterSelectedOptions,
    applyUrl: filterApplyUrl,
    isLoadingPreview,
    toggleOption: handleToggleFilterOption,
    handleApply: handleApplyFilter,
    handleReset: handleResetFilter,
    handleClose: handleCloseFilter,
  } = usePlpFilters({
    listingFilters: plp.listingMeta?.filters ?? [],
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    tags,
    filterParams,
    service: filtersService,
    navigate,
  });

  const {
    previewFilters: desktopPreviewFilters,
    selectedOptions: desktopSelectedOptions,
    isPendingNavigation: isDesktopFilterPending,
    toggleOption: handleDesktopToggleFilter,
    handleReset: handleDesktopResetFilter,
  } = usePlpDesktopFilters({
    listingFilters: plp.listingMeta?.filters ?? [],
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    isLoading: plp.isLoading,
    tags,
    filterParams,
    service: filtersService,
    navigate,
  });

  usePlpRedirectUrlNavigation({ redirectUrl: plp.redirectUrl, sidFromUrl });

  const { videoCallTahItemsCount, maxVideoCallCount, message: videoCallMessage } = useVideoCallCart();

  const handleOpenFilter = useCallback(() => {
    plpAnalytics.trackFilterButtonClicked();
    openFilterSheet();
  }, [plpAnalytics, openFilterSheet]);

  const handleActivateStoreDesignsWithAnalytics = useCallback(() => {
    plpAnalytics.trackDesignsInStoreClick(pincode ?? '');
    setIsNoStoreFound(false);
    handleActivateStoreDesigns();
  }, [plpAnalytics, pincode, handleActivateStoreDesigns]);

  const handleShowAllDesignsAndReset = useCallback(() => {
    setIsNoStoreFound(false);
    handleShowAllDesigns();
  }, [handleShowAllDesigns]);

  const handleChangePincodeForStore = useCallback(() => {
    setPincodeDescription('Browse our exquisite designs: locate stores near you!');
    openPopup((nextSid) => {
      if (nextSid) {
        setIsNoStoreFound(false);
        const urlParams = new URLSearchParams(location.search);
        urlParams.set('sid', nextSid);
        navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` });
      } else {
        setIsNoStoreFound(true);
      }
    });
  }, [openPopup, location.search, location.pathname, navigate]);

  const handleExploreAllDesigns = useCallback(() => {
    setIsNoStoreFound(false);
    handleShowAllDesigns();
  }, [handleShowAllDesigns]);

  const handleApplyFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterApply(previewFilters, filterSelectedOptions);
    handleApplyFilter();
  }, [plpAnalytics, previewFilters, filterSelectedOptions, handleApplyFilter]);

  const handleResetFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterReset(previewFilters, filterSelectedOptions);
    handleResetFilter();
  }, [plpAnalytics, previewFilters, filterSelectedOptions, handleResetFilter]);

  const handleCloseFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterClose();
    handleCloseFilter();
  }, [plpAnalytics, handleCloseFilter]);

  const handleDesktopResetWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterReset(desktopPreviewFilters, desktopSelectedOptions);
    handleDesktopResetFilter();
  }, [plpAnalytics, desktopPreviewFilters, desktopSelectedOptions, handleDesktopResetFilter]);

  const plpCoreProps = {
    ...plp,
    listingMeta: displayListingMeta,
    countLabel: storeCountLabel,
    currentSortBy: sortBy,
    pincode,
    onOpenPincode: handleOpenBottomPincode,
    onOpenSort: handleOpenSortSheet,
    onOpenFilter: handleOpenFilter,
    onApplySort: handleApplySort,
    onSortHover: plpAnalytics.trackSortFilterHovered,
    openPdpInNewTab: isDesktop,
    onActivateStoreDesigns: handleActivateStoreDesignsWithAnalytics,
    onShowAllDesigns: handleShowAllDesignsAndReset,
    isStoreDesignsActive: Boolean(sidFromUrl) || isNoStoreFound,
    isStorePlp: false as const,
    isStoreCitySelectionMode: false as const,
    selectedStoreData: null,
    selectedStoresData: [],
    isStoreWidgetLoading: false as const,
    breadcrumbItems,
    hasStoreLocationWidget: true as const,
    isNoStoreFound,
    onChangePincodeForStore: handleChangePincodeForStore,
    onExploreAllDesigns: handleExploreAllDesigns,
    onProductClick: plpAnalytics.trackProductClick,
    onWishlistClick: plpAnalytics.trackWishlistClick,
    onWishlistToggle: plpAnalytics.trackWishlistToggle,
    onVideoClick: plpAnalytics.trackVideoClick,
    onVideoCallCartAdd: plpAnalytics.trackVideoCallCartAdd,
    onViewDetailsClick: plpAnalytics.trackViewDetailsClick,
    onProductViewed: plpAnalytics.trackListingPageProductViewed,
    viewedProductIds: plpAnalytics.viewedProductIds,
    isDesktopPendingNavigation: isDesktopFilterPending,
    hideVideoCallButton: isGoldCoinsPlp,
  };

  const desktopFilterData = {
    shouldShow: plp.isLoading || desktopPreviewFilters.length > 0,
    filters: desktopPreviewFilters,
    selectedOptions: desktopSelectedOptions,
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    isPendingNavigation: isDesktopFilterPending,
    isStorePlp: false as const,
    currentSortBy: sortBy,
    onToggleOption: handleDesktopToggleFilter,
    onReset: handleDesktopResetWithAnalytics,
    onFilterItemToggle: plpAnalytics.trackDesktopFilterItemSelected,
  };

  const filterSheetProps = {
    open: isFilterSheetOpen,
    filters: previewFilters,
    selectedOptions: filterSelectedOptions,
    applyUrl: filterApplyUrl,
    isLoadingPreview,
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    onToggleOption: handleToggleFilterOption,
    onApply: handleApplyFilterWithAnalytics,
    onReset: handleResetFilterWithAnalytics,
    onClose: handleCloseFilterWithAnalytics,
    onFilterItemToggle: plpAnalytics.trackFilterItemSelected,
    onFilterCategoryExpand: plpAnalytics.trackFilterCategoryExpand,
  };

  const sortSheetProps = {
    open: isSortSheetOpen,
    currentSortBy: sortBy,
    onOpenChange: setIsSortSheetOpen,
    onApply: handleApplySortFromSheet,
    onSortOptionSelect: plpAnalytics.trackSortOptionSelect,
    onCancel: plpAnalytics.trackSortCancel,
  };

  const pincodeProps = {
    open: isPopupOpen,
    onOpenChange: handlePincodeOpenChange,
    inputValue,
    error: pincodeError,
    description: pincodeDescription,
    onChange: handleChange,
    onSubmit: handlePincodeSubmit,
  };

  const videoCallProps = {
    itemsCount: videoCallTahItemsCount,
    maxCount: maxVideoCallCount,
    message: videoCallMessage,
  };

  const showScrollToTop = isDesktop && !isTopVisible;

  return (
    <>
      <div ref={topSentinelRef} aria-hidden />
      <PlpBase
        {...plpCoreProps}
        renderDesktopFilterSidebar={
          desktopFilterData.shouldShow
            ? () => (
                <PlpDesktopFilterSidebar
                  filters={desktopFilterData.filters}
                  selectedOptions={desktopFilterData.selectedOptions}
                  filtersResetUrl={desktopFilterData.filtersResetUrl}
                  isPendingNavigation={desktopFilterData.isPendingNavigation}
                  isStorePlp={desktopFilterData.isStorePlp}
                  currentSortBy={desktopFilterData.currentSortBy}
                  onToggleOption={desktopFilterData.onToggleOption}
                  onReset={desktopFilterData.onReset}
                  onFilterItemToggle={desktopFilterData.onFilterItemToggle}
                />
              )
            : undefined
        }
      />
      <PlpFilterSheet {...filterSheetProps} />
      <PlpSortSheet {...sortSheetProps} />
      <PincodePopup {...pincodeProps} />
      <VideoCallStickyBar {...videoCallProps} />
      <PlpScrollToTopButton show={showScrollToTop} />
    </>
  );
}
